import React, { Fragment } from "react";
import PageContentWrapper from "../../components/wrappers/PageContentWrapper";
import { useTranslation } from "react-i18next";
import { Box, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import { capitalizeFirstLetter } from "../../utils/Functions";
import MapComponent from "../../components/MapComponent";
import { getStaticTimeInfo } from "./components/DateInfoStatic";
import DetailCard from "../../components/display/DetailCard";
import { clinicDetailsArray } from "./enums/ClinicInfoArray";
import ChooseAClinicFullScreenMessage from "../../components/fullScreenMessage/ChooseAClinicFullScreenMessage";
import Banner from "./components/Banner";
import InformationFieldDisplay from "../../components/display/InformationFieldDisplay";
import useAboutUs from "./hooks/useAboutUs";
import useUser from "../../hooks/useUser";
import SimpleAlert from "../../components/display/SimpleAlert";
import { overflowingTitleSx } from "../../enums/Styles";

export default function AboutUs() {
  const aboutUsInfo = useAboutUs();
  const { currentClinic, booleans, isLoading } = useUser();
  const { t } = useTranslation();
  
  if (!currentClinic?.id && !isLoading) {
    return <ChooseAClinicFullScreenMessage />;
  }

  const daysOrder = [
    "MONDAY",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const sortedWorkingHours = aboutUsInfo?.workingDays?.slice().sort((a, b) => {
    return (
      daysOrder.indexOf(capitalizeFirstLetter(a?.day)) -
      daysOrder.indexOf(capitalizeFirstLetter(b?.day))
    );
  });
  const titlesSize = "h5";
  const workingHoursArray = sortedWorkingHours?.map((day) => {
    const shift0Time = getStaticTimeInfo(day, 0, t);
    const shift1Time = getStaticTimeInfo(day, 1, t);

    let value;
    if (shift0Time && shift1Time) {
      value = `${shift0Time} | ${shift1Time}`;
    } else if (shift0Time) {
      value = shift0Time;
    } else if (shift1Time) {
      value = shift1Time;
    } else {
      value = t("NoInfo");
    }

    return {
      title: t(capitalizeFirstLetter(day?.day)),
      value: value,
      testId: `about-us.working-hours.${day?.day?.toUpperCase()}`,
    };
  });

  const clinicArray = clinicDetailsArray(aboutUsInfo?.clinic, t);


  return (
    <PageContentWrapper>
      <Banner />
      <Stack spacing={2}>
        {aboutUsInfo?.clinic?.description && (
          <Fragment>
            <Box>
              <Typography variant={titlesSize} sx={{ mb: 2 }}>
                {t("ClinicInformationMessage")}
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Typography
                  sx={overflowingTitleSx}
                  data-testid="about-us.intro-text"
                >
                  {aboutUsInfo?.clinic?.description}
                </Typography>
              </Paper>
            </Box>
            <Divider />
          </Fragment>
        )}
        {workingHoursArray?.length > 0 ? (
          <>
            <Box>
              <Typography variant={titlesSize} sx={{ mb: 2 }}>
                {t("WorkingHours")}
              </Typography>
              <DetailCard valueArray={workingHoursArray} grid gridCount={3} />
            </Box>
            <Divider />
          </>
        ) : (
          <SimpleAlert
            severity={"warning"}
            message={"ClinicNotWorkingCurrently"}
            testId="about-us.not-working-alert"
          />
        )}

        <Box>
          <MapComponent
            streetAddress={aboutUsInfo?.clinic?.streetAddress}
            city={aboutUsInfo?.clinic?.city}
          />
        </Box>
        <Box>
          <Typography variant={titlesSize} sx={{ mb: 2 }}>
            {t("ContactAndLocation")}
          </Typography>
          <DetailCard valueArray={clinicArray} grid gridCount={2} />
        </Box>
        {aboutUsInfo?.informationFields != null
          ? Object.keys(aboutUsInfo?.informationFields).map(
              (category, informationFields) => (
                <Box>
                  <Typography
                    variant={titlesSize}
                    sx={{ mb: 2 }}
                    data-testid={`about-us.info-fields.category.${category}`}
                  >
                    {t(category)}
                  </Typography>
                  <Grid container spacing={1}>
                    {aboutUsInfo?.informationFields[category].map(
                      (informationField) => (
                        <Grid
                          item
                          sm={
                            aboutUsInfo?.informationFields[category]?.length ===
                            1
                              ? 12
                              : 6
                          }
                          xs={12}
                        >
                          <InformationFieldDisplay
                            informationField={informationField}
                            testId={`about-us.info-fields.field.${informationField.id}`}
                          />
                        </Grid>
                      )
                    )}
                  </Grid>
                </Box>
              )
            )
          : null}
      </Stack>
    </PageContentWrapper>
  );
}
